/**
 * WEICHE ÜBERBLENDUNG BEIM SCHLEIFEN-NEUSTART (Jan 17.10.2026, Scrolly
 * „relax": „den Anfang der Szene — bau eine weiche Überblendung, aber eben
 * nur bei Loop"). Die Echtzeit-Wiedergabe (startRealtimePlayback) springt
 * am Ende hart an den Anfang zurück; der erste Moment der Szene (z. B. ein
 * Einblenden aus dem Dunkeln) wirkte wie ein Vorhang, der aufgeht.
 *
 * Jetzt: im Moment des Rücksprungs wird das LETZTE Bild auf eine eigene,
 * durchsichtige Leinwand über der Bühne kopiert (voller Auflösung, ohne
 * Bilddatei — kein Ruckler), die Szene läuft darunter schon wieder von vorn,
 * und die Leinwand blendet aus. Durchsichtig bleibt durchsichtig: eine
 * CSS-Hintergrundfarbe hinter der Szene scheint weiter durch. Die
 * Ton-Ebenen blenden in derselben Zeit über (alter Ton aus, neuer ein).
 */
import { captureRendererFrame, type Renderer } from '@scrolly/renderer';

export const SCHLEIFEN_BLENDE_MS = 900;

export function schleifenUeberblendung(
  renderer: Renderer | null | undefined,
  dauerMs = SCHLEIFEN_BLENDE_MS,
): void {
  // Ton-Ebenen blenden mit (Kreuzblende, s. AudioLayers.ueberblenden)
  try {
    renderer?.audioLayers?.ueberblenden(dauerMs);
  } catch {
    // Ton ist Kür — die Bild-Blende läuft trotzdem
  }
  const buehne = (
    renderer as unknown as {
      webGLRenderer?: { domElement?: HTMLCanvasElement };
    } | null
  )?.webGLRenderer?.domElement;
  if (
    !renderer ||
    !buehne ||
    !buehne.parentElement ||
    !buehne.width ||
    !buehne.height
  )
    return;
  const blende = document.createElement('canvas');
  blende.width = buehne.width;
  blende.height = buehne.height;
  const ctx = blende.getContext('2d');
  if (!ctx) return;
  try {
    // Am aktuellen (= letzten) Timer frisch rendern und IM Aufnahme-Moment
    // kopieren — ohne preserveDrawingBuffer ist der Puffer sonst leer. Ohne
    // Timer-Override, damit Eigenbewegungen NICHT in die Ruhepose springen.
    // Die Mini-Aufnahme der Funktion selbst (32 px) wird nicht gebraucht.
    captureRendererFrame(renderer as never, 32, undefined, true, () => {
      ctx.drawImage(buehne, 0, 0);
    });
  } catch {
    return;
  }
  blende.setAttribute('aria-hidden', 'true');
  blende.dataset.schleifenBlende = '1';
  Object.assign(blende.style, {
    position: 'absolute',
    left: `${buehne.offsetLeft}px`,
    top: `${buehne.offsetTop}px`,
    width: `${buehne.offsetWidth}px`,
    height: `${buehne.offsetHeight}px`,
    pointerEvents: 'none',
    opacity: '1',
    transition: `opacity ${dauerMs}ms ease`,
    zIndex: getComputedStyle(buehne).zIndex,
  });
  buehne.insertAdjacentElement('afterend', blende);
  // Sofort ausblenden (Layout erzwingen, damit der Übergang greift) und
  // erst NACH dem Ausblenden entfernen — Nachfass-Zeit für langsame Bilder.
  void blende.offsetWidth;
  blende.style.opacity = '0';
  const weg = () => blende.remove();
  blende.addEventListener('transitionend', weg, { once: true });
  setTimeout(weg, dauerMs * 2 + 200);
}
